import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type shaka from 'shaka-player';
import { createAdsController } from '../ads/adsController';
import { attachProgressController } from '../core/controller';
import { attachPlayerKeyboardControls } from '../core/keyboardControls';
import { isLiveStreamPlayback } from '../core/media';
import { attachReplayControl } from '../core/replayControl';
import {
  addExternalCaptionTracksFromVideo,
  addExternalThumbnailsTrack,
  applyDrmConfiguration,
  attachShakaToVideo,
  configureShakaStreaming,
  createShakaPlayer,
  createShakaUIOverlay,
  destroyShakaPlayer,
  destroyShakaUIOverlay,
  formatShakaLoadError,
  installShakaPolyfills,
  isShakaBrowserSupported,
  loadShakaManifest,
} from '../core/shaka';
import { createEventBus } from '../events/eventBus';
import type {
  PlayerUIState,
  SdkPlayerController,
  UsePlayerControllerConfig,
  UsePlayerControllerResult,
} from '../types/player.types';
import { createLiveRetryScheduler } from './liveStreamRetry';

function resolveIsLiveStream(cfg: UsePlayerControllerConfig): boolean {
  if (cfg.playlistItem) return isLiveStreamPlayback(cfg.playlistItem);
  return cfg.isLiveStream;
}

function isValidLiveStream(cfg: UsePlayerControllerConfig): boolean {
  return resolveIsLiveStream(cfg) && !!cfg.manifestUrl;
}

export function usePlayerController(config: UsePlayerControllerConfig): UsePlayerControllerResult {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const shakaRef = useRef<shaka.Player | null>(null);
  const uiOverlayRef = useRef<shaka.ui.Overlay | null>(null);
  /** Avoid effect churn: `drmConfig` is often a new object reference with identical content. */
  const configRef = useRef(config);
  configRef.current = config;
  const bus = useMemo(() => createEventBus(), []);
  const ads = useMemo(() => createAdsController(config.adType ?? 'none'), [config.adType]);

  const [state, setState] = useState<PlayerUIState>({
    loadError: null,
    isBuffering: true,
    isSupported: true,
    isReconnecting: false,
  });
  /** Bumped to tear down + recreate Shaka after a live retry. */
  const [retryGeneration, setRetryGeneration] = useState(0);

  const onSessionReadyRef = useRef(config.onSessionReady);
  onSessionReadyRef.current = config.onSessionReady;
  const onBeforeLoadRef = useRef(config.onBeforeLoad);
  onBeforeLoadRef.current = config.onBeforeLoad;
  const onTeardownRef = useRef(config.onTeardown);
  onTeardownRef.current = config.onTeardown;

  const teardownRef = useRef<(() => void) | null>(null);

  const setReconnecting = useCallback((value: boolean) => {
    setState((s) => (s.isReconnecting === value ? s : { ...s, isReconnecting: value }));
  }, []);

  const showPlaybackError = useCallback((message: string) => {
    setState((s) => ({
      ...s,
      loadError: message,
      isBuffering: false,
      isReconnecting: false,
    }));
  }, []);

  const showPlaybackErrorRef = useRef(showPlaybackError);
  showPlaybackErrorRef.current = showPlaybackError;
  const setReconnectingRef = useRef(setReconnecting);
  setReconnectingRef.current = setReconnecting;

  const liveRetryRef = useRef(
    createLiveRetryScheduler({
      onRetry: () => setRetryGeneration((g) => g + 1),
      showPlaybackError: (message) => showPlaybackErrorRef.current(message),
      setReconnecting: (value) => setReconnectingRef.current(value),
    }),
  );

  // Reset retries when the logical media changes (not on retryGeneration)
  useEffect(() => {
    liveRetryRef.current.reset();
    setRetryGeneration(0);
    setState((s) => ({ ...s, loadError: null, isBuffering: true, isReconnecting: false }));
  }, [config.mediaKey]);

  useEffect(() => {
    return () => {
      liveRetryRef.current.reset();
    };
  }, []);

  const player = useMemo<SdkPlayerController>(() => {
    return {
      seek: (seconds) => {
        const v = videoRef.current;
        if (v) v.currentTime = seconds;
      },
      play: async () => {
        const v = videoRef.current;
        if (v) await v.play().catch(() => undefined);
      },
      pause: () => {
        videoRef.current?.pause();
      },
      load: async (url, start) => {
        const p = shakaRef.current;
        if (!p) throw new Error('Shaka player is not initialized');
        await loadShakaManifest(p, url, start);
      },
      destroy: () => {
        teardownRef.current?.();
      },
      skipAd: () => {
        bus.emit('adSkipRequest');
      },
      on: (e, h) => bus.on(e, h),
      off: (e, h) => bus.off(e, h),
    };
  }, [bus]);

  useEffect(() => {
    const video = videoRef.current;
    const container = containerRef.current;
    if (!video || !container || !config.manifestUrl) return undefined;

    installShakaPolyfills();
    if (!isShakaBrowserSupported()) {
      setState({
        loadError: 'Playback is not supported in this browser.',
        isBuffering: false,
        isSupported: false,
        isReconnecting: false,
      });
      return undefined;
    }

    let cancelled = false;
    let abortedCleanup = false;
    const shakaPlayer = createShakaPlayer();
    shakaRef.current = shakaPlayer;

    const handlePlaybackFailure = (message: string) => {
      if (cancelled) return;

      const cfg = configRef.current;
      if (!isValidLiveStream(cfg)) {
        liveRetryRef.current.reset();
        showPlaybackError(message);
        bus.emit('error', { message });
        return;
      }

      // Suppress immediate error UI while retrying a valid live stream
      setState((s) => ({ ...s, loadError: null, isBuffering: true }));
      const isRetrying = liveRetryRef.current.schedule(message);
      if (!isRetrying) {
        bus.emit('error', { message });
      }
    };

    const onVideoError = () => {
      if (cancelled) return;
      const message = video.error?.message || 'Playback error';
      handlePlaybackFailure(message);
    };
    video.addEventListener('error', onVideoError);

    const disconnectProgressRef = { current: null as (() => void) | null };
    const disconnectKeyboardRef = { current: null as (() => void) | null };
    const disconnectReplayControlRef = { current: null as (() => void) | null };
    const keyboardControlsRef = { current: null as ReturnType<typeof attachPlayerKeyboardControls> | null };
    const adBreakBusHandlersRef = {
      current: null as { start: () => void; end: () => void } | null,
    };

    const cfg = configRef.current;
    const isLiveStream = resolveIsLiveStream(cfg);
    keyboardControlsRef.current = attachPlayerKeyboardControls({
      video,
      container,
      treatAsLive: isLiveStream,
      fullscreenElement: container,
    });
    disconnectKeyboardRef.current = keyboardControlsRef.current.disconnect;
    if (!isLiveStream) {
      disconnectReplayControlRef.current = attachReplayControl(container, video);
    }

    const runLoad = async () => {
      try {
        await attachShakaToVideo(shakaPlayer, video);
        if (cancelled) return;

        const cfg = configRef.current;
        const isLiveStream = resolveIsLiveStream(cfg);

        applyDrmConfiguration(shakaPlayer, cfg.drmConfig);
        configureShakaStreaming(shakaPlayer, isLiveStream);

        /*
         * Mount the Shaka UI overlay BEFORE manifest load so the chrome (spinner, big play button,
         * fade transitions) is visible during the initial fetch — no flash of a bare `<video>`.
         */
        uiOverlayRef.current = createShakaUIOverlay(shakaPlayer, container, video, {
          isLiveStream,
          playlistItem: cfg.playlistItem,
          overrides: cfg.uiOverrides,
        });

        const shakaControls = uiOverlayRef.current.getControls?.();
        if (shakaControls?.toggleFullScreen) {
          keyboardControlsRef.current?.setToggleFullscreen(() => shakaControls.toggleFullScreen());
        }

        await Promise.resolve(
          ads.init({
            video,
            bus,
            shakaPlayer,
            uiOverlay: uiOverlayRef.current,
            adConfig: cfg.adsConfig,
          }),
        );
        if (cancelled) return;

        const onAdBreakStart = () => keyboardControlsRef.current?.setSeekBlocked(true);
        const onAdBreakEnd = () => keyboardControlsRef.current?.setSeekBlocked(false);
        bus.on('adBreakStart', onAdBreakStart);
        bus.on('adBreakEnd', onAdBreakEnd);
        adBreakBusHandlersRef.current = { start: onAdBreakStart, end: onAdBreakEnd };

        const { disconnect: disconnectProgress } = attachProgressController(video, {
          treatAsLive: isLiveStream,
          bus,
        });
        disconnectProgressRef.current = disconnectProgress;

        onSessionReadyRef.current?.(player);

        onBeforeLoadRef.current?.();
        bus.emit('beforeLoad');
        const startPosition =
          isLiveStream || !cfg.startTime || cfg.startTime <= 0 ? undefined : cfg.startTime;
        await loadShakaManifest(shakaPlayer, cfg.manifestUrl!, startPosition);
        if (cancelled) return;

        /*
         * Thumbnail side-load runs strictly after `load()` resolves; manifest duration must exist
         * (Shaka throws CANNOT_ADD_EXTERNAL_THUMBNAILS_TO_LIVE_STREAM otherwise — gated in helper).
         */
        await addExternalThumbnailsTrack(shakaPlayer, cfg.trickplayThumbnailsUrl, isLiveStream);
        if (cancelled) return;

        await addExternalCaptionTracksFromVideo(shakaPlayer, video, isLiveStream, cfg.sidecarCaptionTracks);
        if (cancelled) return;

        if (cfg.adType === 'csai') {
          await ads.requestAds?.();
        }

        // Playback recovered — stop retries and remove connecting label
        liveRetryRef.current.reset();
        setState((s) => ({
          ...s,
          loadError: null,
          isBuffering: false,
          isSupported: true,
          isReconnecting: false,
        }));
        bus.emit('loaded');
        if (cfg.autostart !== false) {
          await video.play().catch(() => undefined);
        }
      } catch (e) {
        if (cancelled) return;
        const msg = formatShakaLoadError(e);
        handlePlaybackFailure(msg);
        console.error(e);
      }
    };

    void runLoad();

    const cleanup = () => {
      if (abortedCleanup) return;
      abortedCleanup = true;
      cancelled = true;
      video.removeEventListener('error', onVideoError);
      if (adBreakBusHandlersRef.current) {
        bus.off('adBreakStart', adBreakBusHandlersRef.current.start);
        bus.off('adBreakEnd', adBreakBusHandlersRef.current.end);
        adBreakBusHandlersRef.current = null;
      }
      disconnectProgressRef.current?.();
      disconnectProgressRef.current = null;
      disconnectKeyboardRef.current?.();
      disconnectKeyboardRef.current = null;
      disconnectReplayControlRef.current?.();
      disconnectReplayControlRef.current = null;
      ads.destroy?.();
      /* Destroy UI overlay BEFORE the player; otherwise its event listeners can throw on a torn-down player. */
      void destroyShakaUIOverlay(uiOverlayRef.current);
      uiOverlayRef.current = null;
      void destroyShakaPlayer(shakaPlayer).catch(() => undefined);
      shakaRef.current = null;
      teardownRef.current = null;
      onTeardownRef.current?.();
    };

    teardownRef.current = cleanup;

    return cleanup;
    /*
     * Do NOT list `config.startTime` or `config.drmConfig` here:
     * - VOD: `startTime` from watch-history is derived from `progress`, which updates during playback → would
     *   destroy/recreate Shaka every save and freeze the UI after any interaction.
     * - `drmConfig` is often a new object reference with identical content.
     * Initial start position + DRM always come from `configRef.current` inside `runLoad`.
     */
  }, [
    ads,
    bus,
    config.adType,
    config.adsConfig?.schedule,
    config.autostart,
    config.isLiveStream,
    config.manifestUrl,
    config.mediaKey,
    config.playlistItem,
    player,
    retryGeneration,
    showPlaybackError,
  ]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !config.manifestUrl) return undefined;

    let firstFrameSent = false;
    const emitFirstFrame = () => {
      if (firstFrameSent) return;
      firstFrameSent = true;
      bus.emit('firstFrame');
    };

    const onPlay = () => bus.emit('play');
    const onEnded = () => bus.emit('complete');
    const onPlaying = () => {
      setState((s) => ({ ...s, isBuffering: false }));
      bus.emit('buffer', { buffering: false });
      emitFirstFrame();
    };
    const onWaiting = () => {
      setState((s) => ({ ...s, isBuffering: true }));
      bus.emit('buffer', { buffering: true });
    };
    const onCanPlay = () => {
      setState((s) => ({ ...s, isBuffering: false }));
      bus.emit('buffer', { buffering: false });
    };
    const onSeeked = () => {
      setState((s) => ({ ...s, isBuffering: false }));
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('ended', onEnded);
    video.addEventListener('playing', onPlaying);
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('canplay', onCanPlay);
    video.addEventListener('seeked', onSeeked);

    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('seeked', onSeeked);
    };
  }, [bus, config.manifestUrl, config.mediaKey, retryGeneration]);

  return {
    videoRef,
    containerRef,
    state,
    player,
    shakaPlayerRef: shakaRef,
  };
}
